import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { queryClient, makePersister, CACHE_MAX_AGE_MS } from "./lib/queryClient";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useTheme } from "./context/ThemeContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CartProvider } from "./context/CartContext";
import { FeatureFlagsProvider } from "./context/FeatureFlagsContext";
import { RegionProvider } from "./context/RegionContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import { PWAInstallPill } from "./components/PWAInstallPill";
import { PWAInstallHeaderPill } from "./components/PWAInstallHeaderPill";
import { BottomTabBar } from "./components/BottomTabBar";
import { DispatchLeftRail } from "./components/DispatchLeftRail";
import { GlobalUserSearch } from "./components/GlobalUserSearch";
import { capturePendingRef, capturePreviewInstall } from "./utils/installPwa";
import { installToastDefaults } from "./utils/toastDefaults";
import { usePresenceHeartbeat } from "./hooks/usePresenceHeartbeat";
import { usePageViewTracker } from "./hooks/usePageViewTracker";
import { usePwaHeartbeat } from "./hooks/usePwaHeartbeat";
import { useSSECacheBridge } from "./hooks/useSSECacheBridge";
import { PendingDeepLinkResolver } from "./components/PendingDeepLinkResolver";

// Apply global toast behavior overrides (8s duration, error persists until tap)
installToastDefaults();

// Phase 7-lite — Capture ?ref=ABC from QR landing URLs as soon as the app
// boots so the code survives any accidental redirect (e.g. mis-tap of the
// install banner area sending the user to a clean /register URL).
capturePendingRef();
// Phase 7-lite — `?preview_install=1` lets operators preview the install
// flow inside the already-installed PWA (for demos / iteration).
capturePreviewInstall();

// Auth Pages
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ContactPage from "./pages/ContactPage";
import CustomerDashboardPreview from "./pages/preview/CustomerDashboardPreview";
import TierCardPreview from "./pages/_preview/TierCardPreview";
import TierEmailArt from "./pages/_preview/TierEmailArt";
import { PrivacyPage, TermsPage } from "./pages/LegalPages";
import { AgeGate } from "./components/AgeGate";
import DesignPreviewPage from "./pages/DesignPreviewPage";

// Customer Pages
import { CustomerDashboardShell as CustomerDashboard } from "./pages/customer/CustomerDashboardShell";
import AtRiskCustomersPage from "./pages/dispatch/AtRiskCustomersPage";
import { CartPageShell as CartPage } from "./pages/customer/CartPageShell";
import { OrdersPageShell as OrdersPage } from "./pages/customer/OrdersPageShell";
import { OrderDetailsPageShell as OrderDetailsPage } from "./pages/customer/OrderDetailsPageShell";
import { ProfilePageShell as ProfilePage } from "./pages/customer/ProfilePageShell";
import { AccountSecurityPage } from "./pages/AccountSecurityPage";

// Driver Pages
import { DriverDashboard } from "./pages/driver/DriverDashboard";
import { DriverProfilePage } from "./pages/driver/DriverProfilePage";

// Dispatch Pages
import { DispatchDashboard } from "./pages/dispatch/DispatchDashboard";
import { InventoryPage } from "./pages/dispatch/InventoryPage";
import { SettingsPage } from "./pages/dispatch/SettingsPage";
import { BroadcastPage } from "./pages/dispatch/BroadcastPage";
import { RegionsAdminPage } from "./pages/dispatch/RegionsAdminPage";
import { PointsAdminPage } from "./pages/dispatch/PointsAdminPage";
import { UsersAdminPage } from "./pages/dispatch/UsersAdminPage";
import FoundersThanksPage from "./pages/dispatch/FoundersThanksPage";
import FailedOrdersPage from "./pages/dispatch/FailedOrdersPage";
import DeliverySlotsPage from "./pages/dispatch/DeliverySlotsPage";
import PointsWatchPage from "./pages/dispatch/PointsWatchPage";
import DataQualityPage from "./pages/dispatch/DataQualityPage";
import PromoAgentPage from "./pages/dispatch/PromoAgentPage";
import EditProfilePage from "./pages/dispatch/EditProfilePage";
import PushHealthPage from "./pages/dispatch/PushHealthPage";
import EmailHealthPage from "./pages/dispatch/EmailHealthPage";
import DailyBriefArchivePage from "./pages/dispatch/DailyBriefArchivePage";
import LoginStatsPage from "./pages/dispatch/LoginStatsPage";
import { LeaderboardsPage } from "./pages/dispatch/LeaderboardsPage";
import { LaunchStatsPage } from "./pages/dispatch/LaunchStatsPage";
import ReviewModerationPage from "./pages/dispatch/ReviewModerationPage";

// Phase 5.2.1 — Referrals & Staff QR
import { RewardsHub as ReferralPage } from "./pages/customer/RewardsHub";
import { StaffRewardsPage } from "./pages/staff/StaffRewardsPage";
import CustomerChatBubble from "./components/CustomerChatBubble";

function PresenceWiring() {
  const { user } = useAuth();
  const isAuthed = !!(user && user.id);
  usePresenceHeartbeat(isAuthed);
  usePageViewTracker(isAuthed);
  usePwaHeartbeat(isAuthed);
  // Feb 2026 — Phase 4. Bridge live SSE events into the React Query cache
  // so any open list/detail view patches itself the moment the backend
  // emits an order/product change. Mounted here so a single subscription
  // covers every route.
  useSSECacheBridge();
  return null;
}

const TOAST_BASE = {
  padding: '14px 16px',
  paddingRight: '40px',
  minHeight: '64px',
  maxWidth: '92vw',
  width: 'auto',
};
const TOAST_STYLE = {
  code: { ...TOAST_BASE, background: '#0C0612', border: '1px solid #39FF14', color: '#39FF14', fontFamily: 'JetBrains Mono, monospace', fontSize: '14px' },
  // Modern: solid dark ink on the light canvas for maximum legibility.
  modern: { ...TOAST_BASE, background: '#0F1712', border: '1px solid #0F1712', color: '#FFFFFF', fontFamily: 'Inter, -apple-system, sans-serif', fontSize: '15px', fontWeight: 500, borderRadius: '14px', boxShadow: '0 12px 32px rgba(15,23,18,0.28)' },
};

function ThemedToaster() {
  const { isModern } = useTheme();
  return (
    <Toaster
      position="top-center"
      offset="calc(env(safe-area-inset-top, 0px) + 32px)"
      duration={8000}
      closeButton
      toastOptions={{ style: TOAST_STYLE[isModern ? 'modern' : 'code'], className: isModern ? 'am-toast-modern' : '' }}
    />
  );
}

function GlobalCustomerChat() {
  const { user } = useAuth();
  if (!user || user.role !== 'customer') return null;
  return <CustomerChatBubble user={user} />;
}

function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: makePersister(),
        maxAge: CACHE_MAX_AGE_MS,
        // Bump buster to force-invalidate persisted cache on schema/logic changes.
        buster: 'ash-merch-2026-02-27',
      }}
    >
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
        <FeatureFlagsProvider>
          <RegionProvider>
            <CartProvider>
          <AgeGate>
          <PresenceWiring />
          <PendingDeepLinkResolver />
          <GlobalCustomerChat />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/preview/dashboard" element={<CustomerDashboardPreview />} />
            <Route path="/preview/tier-card" element={<TierCardPreview />} />
            <Route path="/preview/tier-art" element={<TierEmailArt />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/design-preview" element={<DesignPreviewPage />} />

            {/* Customer Routes */}
            <Route 
              path="/customer" 
              element={
                <ProtectedRoute allowedRoles={["customer"]}>
                  <CustomerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/customer/cart" 
              element={
                <ProtectedRoute allowedRoles={["customer"]}>
                  <CartPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/customer/orders" 
              element={
                <ProtectedRoute allowedRoles={["customer"]}>
                  <OrdersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/customer/orders/:orderId" 
              element={
                <ProtectedRoute allowedRoles={["customer"]}>
                  <OrderDetailsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/customer/profile" 
              element={
                <ProtectedRoute allowedRoles={["customer"]}>
                  <ProfilePage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/customer/refer"
              element={
                <ProtectedRoute allowedRoles={["customer"]}>
                  <ReferralPage />
                </ProtectedRoute>
              }
            />

            {/* Staff Rewards (driver / dispatcher / super-admin) */}
            <Route
              path="/staff/rewards"
              element={
                <ProtectedRoute allowedRoles={["driver", "dispatcher", "super_admin"]}>
                  <StaffRewardsPage />
                </ProtectedRoute>
              }
            />

            {/* Account Security — accessible by ANY authenticated role.
                The only place dispatcher/super_admin can change their own
                password or login email. */}
            <Route
              path="/account/security"
              element={
                <ProtectedRoute allowedRoles={["customer", "driver", "dispatcher", "super_admin"]}>
                  <AccountSecurityPage />
                </ProtectedRoute>
              }
            />

            {/* Driver Routes */}
            <Route 
              path="/driver" 
              element={
                <ProtectedRoute allowedRoles={["driver"]}>
                  <DriverDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/driver/profile" 
              element={
                <ProtectedRoute allowedRoles={["driver"]}>
                  <DriverProfilePage />
                </ProtectedRoute>
              } 
            />

            {/* Dispatch Routes */}
            <Route 
              path="/dispatch" 
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <DispatchDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dispatch/inventory" 
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <InventoryPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dispatch/settings" 
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dispatch/broadcast" 
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <BroadcastPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dispatch/admin/regions" 
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <RegionsAdminPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dispatch/admin/points" 
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <PointsAdminPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/dispatch/admin/users"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <UsersAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dispatch/admin/leaderboards"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <LeaderboardsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dispatch/admin/launch-stats"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <LaunchStatsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dispatch/admin/reviews"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <ReviewModerationPage />
                </ProtectedRoute>
              }
            />

            {/* Phase 6.0.4 — Renamed "Founder's Thanks" → "BOOST". Old URL
                kept as a redirect so any bookmarks / email links from the
                pre-rename era still land on the right place. */}
            <Route
              path="/dispatch/admin/boost"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <FoundersThanksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dispatch/admin/founders-thanks"
              element={<Navigate to="/dispatch/admin/boost" replace />}
            />

            <Route
              path="/dispatch/admin/points-watch"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <PointsWatchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dispatch/admin/delivery-slots"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <DeliverySlotsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dispatch/admin/failed-orders"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <FailedOrdersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dispatch/admin/data-quality"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <DataQualityPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dispatch/admin/at-risk-customers"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <AtRiskCustomersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dispatch/admin/promo-agent"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <PromoAgentPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dispatch/admin/edit-profile"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <EditProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dispatch/admin/push-health"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <PushHealthPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dispatch/admin/email-health"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <EmailHealthPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dispatch/admin/daily-brief-archive"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <DailyBriefArchivePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dispatch/admin/login-stats"
              element={
                <ProtectedRoute allowedRoles={["dispatcher", "super_admin"]}>
                  <LoginStatsPage />
                </ProtectedRoute>
              }
            />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <ThemedToaster />
          <PWAInstallPill />
          <PWAInstallHeaderPill />
          <BottomTabBar />
          <DispatchLeftRail />
          <GlobalUserSearch />
          </AgeGate>
          </CartProvider>
          </RegionProvider>
        </FeatureFlagsProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
    </PersistQueryClientProvider>
  );
}

export default App;
